import { Box, Flex, Icon, SimpleGrid, Text } from '@chakra-ui/react';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import React from 'react';

import { PhotoEntry } from '../buildPhotoSlotMap';
import { CategoryDefinition, PHOTO_SLOTS, SlotDefinition } from '../constants';
import PhotoSlot from './PhotoSlot';

type CategorySectionProps = {
  category: CategoryDefinition;
  photos: Map<string, PhotoEntry>;
  processingSlotIds: Set<string>;
  pulsingSlotId: string | null;
  onSlotClick: (slotId: string) => void;
  onViewPhoto: (slotId: string) => void;
  onDelete: (slotId: string) => void;
  onFileDrop: (slotId: string, file: File) => void;
  slotRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  isPostAcceptance?: boolean;
};

const CategorySection = ({
  category,
  photos,
  processingSlotIds,
  pulsingSlotId,
  onSlotClick,
  onViewPhoto,
  onDelete,
  onFileDrop,
  slotRefs,
  isPostAcceptance = false,
}: CategorySectionProps) => {
  const slots: SlotDefinition[] = PHOTO_SLOTS.filter((s) => s.category === category.key);
  const filledCount = slots.filter((s) => photos.has(s.id)).length;
  const isComplete = filledCount === slots.length;

  return (
    <Box mb={8}>
      <Flex align="baseline" justify="space-between" mb={3}>
        <Box>
          <Text fontSize="lg" fontWeight="700" color="grayDark">
            {category.title}
          </Text>
          <Text fontSize="sm" color="grayLight">
            {category.subtitle}
          </Text>
        </Box>
        <Flex align="center" gap={1.5} flexShrink={0}>
          {isComplete && <Icon as={FaCheck} boxSize="12px" color="leaseEndGreen" />}
          <Text fontSize="sm" fontWeight="600" color={isComplete ? 'leaseEndGreen' : 'grayLight'}>
            {filledCount} / {slots.length}
          </Text>
        </Flex>
      </Flex>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        {slots.map((slot) => (
          <PhotoSlot
            key={slot.id}
            slot={slot}
            photo={photos.get(slot.id)}
            isProcessing={processingSlotIds.has(slot.id)}
            isPulsing={pulsingSlotId === slot.id}
            onClick={() => onSlotClick(slot.id)}
            onViewPhoto={() => onViewPhoto(slot.id)}
            onDelete={() => onDelete(slot.id)}
            onFileDrop={(file) => onFileDrop(slot.id, file)}
            isPostAcceptance={isPostAcceptance}
            slotRef={(el) => {
              if (el) {
                slotRefs.current.set(slot.id, el);
              } else {
                slotRefs.current.delete(slot.id);
              }
            }}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default CategorySection;
